import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Gift, Settings, Users, Award, TrendingUp, Plus, Edit3, Trash2, Save, X, Loader2, Percent, DollarSign, Calendar, Star, ShoppingCart, Ticket, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import DashboardHeader from '../components/DashboardHeader';
import BreadcrumbNav from '../components/BreadcrumbNav';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LoyaltyPoints() {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const { language } = useLanguage();
  
  // Active Tab
  const [activeTab, setActiveTab] = useState('settings');
  
  // Settings State
  const [settings, setSettings] = useState({
    enabled: true,
    points_per_dirham: 1,
    min_points_to_redeem: 100,
    points_value_in_dirham: 0.1,
    expiry_days: 365,
    welcome_bonus: 50,
    referral_bonus: 100,
    birthday_bonus: 200,
  });
  
  // Tiers State
  const [tiers, setTiers] = useState([
    { id: '1', name_ar: 'برونزي', name_en: 'Bronze', min_points: 0, discount_percent: 0, color: '#cd7f32' },
    { id: '2', name_ar: 'فضي', name_en: 'Silver', min_points: 500, discount_percent: 5, color: '#c0c0c0' },
    { id: '3', name_ar: 'ذهبي', name_en: 'Gold', min_points: 2000, discount_percent: 10, color: '#ffd700' },
    { id: '4', name_ar: 'بلاتيني', name_en: 'Platinum', min_points: 5000, discount_percent: 15, color: '#e5e4e2' },
  ]);
  
  // Campaigns State
  const [campaigns, setCampaigns] = useState([]);
  
  // Users with Points State
  const [usersWithPoints, setUsersWithPoints] = useState([]);
  
  // Loading States
  const [loading, setLoading] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Modals
  const [showTierModal, setShowTierModal] = useState(false);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [showRedeemModal, setShowRedeemModal] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  const [editingCampaign, setEditingCampaign] = useState(null);
  
  // Redeem Code State
  const [redeemCode, setRedeemCode] = useState('');
  const [redeemLoading, setRedeemLoading] = useState(false);
  const [redeemResult, setRedeemResult] = useState(null);
  
  // New Tier Form
  const [newTier, setNewTier] = useState({
    name_ar: '',
    name_en: '',
    min_points: 0,
    discount_percent: 0,
    color: '#c9a227'
  });
  
  // New Campaign Form
  const [newCampaign, setNewCampaign] = useState({
    name_ar: '',
    name_en: '',
    multiplier: 2,
    start_date: '',
    end_date: '',
    description_ar: '',
    description_en: '',
    is_active: true
  });
  
  // Fetch Data
  useEffect(() => {
    fetchLoyaltyData();
  }, [token]);
  
  const fetchLoyaltyData = async () => {
    if (!token) return;
    setLoading(true);
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch settings
      const settingsRes = await axios.get(`${API_URL}/api/admin/loyalty/settings`, { headers }).catch(() => ({ data: { data: settings } }));
      if (settingsRes.data?.data) {
        setSettings(settingsRes.data.data);
      }
      
      // Fetch tiers
      const tiersRes = await axios.get(`${API_URL}/api/admin/loyalty/tiers`, { headers }).catch(() => ({ data: { data: tiers } }));
      if (tiersRes.data?.data) {
        setTiers(tiersRes.data.data);
      }
      
      // Fetch campaigns
      const campaignsRes = await axios.get(`${API_URL}/api/admin/loyalty/campaigns`, { headers }).catch(() => ({ data: { data: [] } }));
      if (campaignsRes.data?.data) {
        setCampaigns(campaignsRes.data.data);
      }
      
      // Fetch users with points
      const usersRes = await axios.get(`${API_URL}/api/admin/loyalty/users`, { headers }).catch(() => ({ data: { data: [] } }));
      if (usersRes.data?.data) {
        setUsersWithPoints(usersRes.data.data);
      }
      
    } catch (error) {
      console.error('Error fetching loyalty data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Save Settings
  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API_URL}/api/admin/loyalty/settings`, settings, { headers });
      toast.success(language === 'ar' ? 'تم حفظ الإعدادات بنجاح' : 'Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error(language === 'ar' ? 'فشل في حفظ الإعدادات' : 'Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };
  
  // Save Tier
  const saveTier = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const tierData = editingTier ? { ...editingTier, ...newTier } : { ...newTier, id: Date.now().toString() };
      
      if (editingTier) {
        await axios.put(`${API_URL}/api/admin/loyalty/tiers/${editingTier.id}`, tierData, { headers }).catch(() => {});
        setTiers(prev => prev.map(t => t.id === editingTier.id ? tierData : t));
      } else {
        await axios.post(`${API_URL}/api/admin/loyalty/tiers`, tierData, { headers }).catch(() => {});
        setTiers(prev => [...prev, tierData]);
      }
      
      toast.success(language === 'ar' ? 'تم حفظ المستوى بنجاح' : 'Tier saved successfully');
      setShowTierModal(false);
      setEditingTier(null);
      setNewTier({ name_ar: '', name_en: '', min_points: 0, discount_percent: 0, color: '#c9a227' });
    } catch (error) {
      toast.error(language === 'ar' ? 'فشل في حفظ المستوى' : 'Failed to save tier');
    }
  };
  
  // Delete Tier
  const deleteTier = async (tierId) => {
    if (!window.confirm(language === 'ar' ? 'هل أنت متأكد من حذف هذا المستوى؟' : 'Are you sure you want to delete this tier?')) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API_URL}/api/admin/loyalty/tiers/${tierId}`, { headers }).catch(() => {});
      setTiers(prev => prev.filter(t => t.id !== tierId));
      toast.success(language === 'ar' ? 'تم حذف المستوى بنجاح' : 'Tier deleted successfully');
    } catch (error) {
      toast.error(language === 'ar' ? 'فشل في حذف المستوى' : 'Failed to delete tier');
    }
  };
  
  // Save Campaign
  const saveCampaign = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const campaignData = editingCampaign ? { ...editingCampaign, ...newCampaign } : { ...newCampaign, id: Date.now().toString() };
      
      if (editingCampaign) {
        await axios.put(`${API_URL}/api/admin/loyalty/campaigns/${editingCampaign.id}`, campaignData, { headers }).catch(() => {});
        setCampaigns(prev => prev.map(c => c.id === editingCampaign.id ? campaignData : c));
      } else {
        await axios.post(`${API_URL}/api/admin/loyalty/campaigns`, campaignData, { headers }).catch(() => {});
        setCampaigns(prev => [...prev, campaignData]);
      }
      
      toast.success(language === 'ar' ? 'تم حفظ الحملة بنجاح' : 'Campaign saved successfully');
      setShowCampaignModal(false);
      setEditingCampaign(null);
      setNewCampaign({ name_ar: '', name_en: '', multiplier: 2, start_date: '', end_date: '', description_ar: '', description_en: '', is_active: true });
    } catch (error) {
      toast.error(language === 'ar' ? 'فشل في حفظ الحملة' : 'Failed to save campaign');
    }
  };
  
  // Redeem Code
  const redeemCodeHandler = async () => {
    if (!redeemCode.trim()) {
      toast.error(language === 'ar' ? 'يرجى إدخال الرمز' : 'Please enter a code');
      return;
    }
    
    setRedeemLoading(true);
    setRedeemResult(null);
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(`${API_URL}/api/admin/loyalty/redeem-code`, {
        code: redeemCode.trim().toUpperCase()
      }, { headers });
      
      if (response.data.success) {
        setRedeemResult({
          success: true,
          points: response.data.points,
          message: response.data.message,
          user: response.data.user
        });
        toast.success(language === 'ar' ? 'تم استرداد الرمز بنجاح!' : 'Code redeemed successfully!');
      } else {
        setRedeemResult({
          success: false,
          message: response.data.message || (language === 'ar' ? 'فشل في استرداد الرمز' : 'Failed to redeem code')
        });
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || (language === 'ar' ? 'رمز غير صالح أو منتهي الصلاحية' : 'Invalid or expired code');
      setRedeemResult({
        success: false,
        message: errorMsg
      });
      toast.error(errorMsg);
    } finally {
      setRedeemLoading(false);
    }
  };
  
  // Reset Redeem Modal
  const resetRedeemModal = () => {
    setShowRedeemModal(false);
    setRedeemCode('');
    setRedeemResult(null);
  };
  
  // Statistics
  const totalPoints = usersWithPoints.reduce((sum, u) => sum + (u.points || 0), 0);
  const totalUsers = usersWithPoints.length;
  const activeUsers = usersWithPoints.filter(u => (u.points || 0) > 0).length;

  return (
    <div className="min-h-screen bg-[#030303]">
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-[#d4af37]/5 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-rose-500/3 rounded-full blur-[100px]" />
      </div>

      {/* Header */}
      <DashboardHeader 
        showBackButton={true}
        backPath="/dashboard/finance/cloudgm/customers"
      />

      {/* Breadcrumb with border */}
      <div className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <BreadcrumbNav items={[
            { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
            { label_ar: 'المالية', label_en: 'Finance', path: '/dashboard/finance' },
            { label_ar: 'CLOUDGM FZ LLC', label_en: 'CLOUDGM FZ LLC', path: '/dashboard/finance/cloudgm' },
            { label_ar: 'منصة العملاء (المالية)', label_en: 'Customer Platform (Finance)', path: '/dashboard/finance/cloudgm/customers' },
            { label_ar: 'التحكم بالإنفاق', label_en: 'Expense Control', path: null }
          ]} />
        </div>
      </div>

      {/* Title Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-rose-500/10 rounded-xl flex items-center justify-center border border-rose-500/20">
            <Gift className="h-6 w-6 text-rose-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">
              {language === 'ar' ? 'التحكم بنقاط الولاء' : 'Loyalty Points Control'}
            </h1>
            <p className="text-sm text-[#71717A]">
              {language === 'ar' ? 'إدارة برنامج نقاط الولاء' : 'Manage loyalty program'}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Statistics Cards */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8"
        >
          <div className="bg-white/[0.02] backdrop-blur-xl rounded-2xl p-4 border border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-rose-500/10 rounded-xl flex items-center justify-center border border-rose-500/20">
                <Star className="h-6 w-6 text-rose-400" />
              </div>
              <div>
                <p className="text-[#71717A] text-xs">{language === 'ar' ? 'إجمالي النقاط' : 'Total Points'}</p>
                <p className="text-white font-bold text-lg">{totalPoints.toLocaleString()}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white/[0.02] backdrop-blur-xl rounded-2xl p-4 border border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-emerald-500/10 rounded-xl flex items-center justify-center border border-emerald-500/20">
                <Users className="h-6 w-6 text-emerald-400" />
              </div>
              <div>
                <p className="text-[#71717A] text-xs">{language === 'ar' ? 'المستخدمين النشطين' : 'Active Users'}</p>
                <p className="text-white font-bold text-lg">{activeUsers}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white/[0.02] backdrop-blur-xl rounded-2xl p-4 border border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-[#d4af37]/10 rounded-xl flex items-center justify-center border border-[#d4af37]/20">
                <Award className="h-6 w-6 text-[#d4af37]" />
              </div>
              <div>
                <p className="text-[#71717A] text-xs">{language === 'ar' ? 'مستويات الولاء' : 'Loyalty Tiers'}</p>
                <p className="text-white font-bold text-lg">{tiers.length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white/[0.02] backdrop-blur-xl rounded-2xl p-4 border border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-purple-500/10 rounded-xl flex items-center justify-center border border-purple-500/20">
                <TrendingUp className="h-6 w-6 text-purple-400" />
              </div>
              <div>
                <p className="text-[#71717A] text-xs">{language === 'ar' ? 'الحملات النشطة' : 'Active Campaigns'}</p>
                <p className="text-white font-bold text-lg">{campaigns.filter(c => c.is_active).length}</p>
              </div>
            </div>
          </div>
        </motion.div>
        
        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: 'settings', label: language === 'ar' ? 'الإعدادات' : 'Settings', icon: Settings },
            { id: 'tiers', label: language === 'ar' ? 'المستويات' : 'Tiers', icon: Award },
            { id: 'campaigns', label: language === 'ar' ? 'الحملات' : 'Campaigns', icon: Gift },
            { id: 'users', label: language === 'ar' ? 'المستخدمين' : 'Users', icon: Users },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-colors whitespace-nowrap ${
                activeTab === tab.id 
                  ? 'bg-[#f43f5e] text-white' 
                  : 'bg-[#1a1a2e] text-[#808080] hover:bg-[#2a2a4e] border border-[#2a2a4e]'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>
        
        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-[#f43f5e]" />
          </div>
        ) : (
          <>
            {/* Settings Tab */}
            {activeTab === 'settings' && (
              <div className="bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e] p-6">
                <h2 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                  <Settings className="h-5 w-5 text-[#f43f5e]" />
                  {language === 'ar' ? 'إعدادات نقاط الولاء' : 'Loyalty Points Settings'}
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Enable/Disable */}
                  <div className="col-span-full">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.enabled}
                        onChange={(e) => setSettings({...settings, enabled: e.target.checked})}
                        className="w-5 h-5 rounded border-[#2a2a4e] bg-[#0a0a0a] text-[#f43f5e] focus:ring-[#f43f5e]"
                      />
                      <span className="text-white font-medium">
                        {language === 'ar' ? 'تفعيل برنامج نقاط الولاء' : 'Enable Loyalty Points Program'}
                      </span>
                    </label>
                  </div>
                  
                  {/* Points per Dirham */}
                  <div>
                    <label className="block text-sm text-[#808080] mb-2">
                      {language === 'ar' ? 'نقاط لكل درهم' : 'Points per Dirham'}
                    </label>
                    <Input
                      type="number"
                      value={settings.points_per_dirham}
                      onChange={(e) => setSettings({...settings, points_per_dirham: Number(e.target.value)})}
                      className="bg-[#0a0a0a] border-[#2a2a4e] text-white"
                    />
                  </div>
                  
                  {/* Min Points to Redeem */}
                  <div>
                    <label className="block text-sm text-[#808080] mb-2">
                      {language === 'ar' ? 'الحد الأدنى للاستبدال' : 'Min Points to Redeem'}
                    </label>
                    <Input
                      type="number"
                      value={settings.min_points_to_redeem}
                      onChange={(e) => setSettings({...settings, min_points_to_redeem: Number(e.target.value)})}
                      className="bg-[#0a0a0a] border-[#2a2a4e] text-white"
                    />
                  </div>
                  
                  {/* Points Value */}
                  <div>
                    <label className="block text-sm text-[#808080] mb-2">
                      {language === 'ar' ? 'قيمة النقطة بالدرهم' : 'Point Value in Dirham'}
                    </label>
                    <Input
                      type="number"
                      step="0.01"
                      value={settings.points_value_in_dirham}
                      onChange={(e) => setSettings({...settings, points_value_in_dirham: Number(e.target.value)})}
                      className="bg-[#0a0a0a] border-[#2a2a4e] text-white"
                    />
                  </div>
                  
                  {/* Expiry Days */}
                  <div>
                    <label className="block text-sm text-[#808080] mb-2">
                      {language === 'ar' ? 'صلاحية النقاط (أيام)' : 'Points Expiry (Days)'}
                    </label>
                    <Input
                      type="number"
                      value={settings.expiry_days}
                      onChange={(e) => setSettings({...settings, expiry_days: Number(e.target.value)})}
                      className="bg-[#0a0a0a] border-[#2a2a4e] text-white"
                    />
                  </div>
                  
                  {/* Welcome Bonus */}
                  <div>
                    <label className="block text-sm text-[#808080] mb-2">
                      {language === 'ar' ? 'نقاط الترحيب' : 'Welcome Bonus'}
                    </label>
                    <Input
                      type="number"
                      value={settings.welcome_bonus}
                      onChange={(e) => setSettings({...settings, welcome_bonus: Number(e.target.value)})}
                      className="bg-[#0a0a0a] border-[#2a2a4e] text-white"
                    />
                  </div>
                  
                  {/* Referral Bonus */}
                  <div>
                    <label className="block text-sm text-[#808080] mb-2">
                      {language === 'ar' ? 'نقاط الإحالة' : 'Referral Bonus'}
                    </label>
                    <Input
                      type="number"
                      value={settings.referral_bonus}
                      onChange={(e) => setSettings({...settings, referral_bonus: Number(e.target.value)})}
                      className="bg-[#0a0a0a] border-[#2a2a4e] text-white"
                    />
                  </div>
                  
                  {/* Birthday Bonus */}
                  <div>
                    <label className="block text-sm text-[#808080] mb-2">
                      {language === 'ar' ? 'نقاط عيد الميلاد' : 'Birthday Bonus'}
                    </label>
                    <Input
                      type="number"
                      value={settings.birthday_bonus}
                      onChange={(e) => setSettings({...settings, birthday_bonus: Number(e.target.value)})}
                      className="bg-[#0a0a0a] border-[#2a2a4e] text-white"
                    />
                  </div>
                </div>
                
                <div className="mt-6 flex justify-end">
                  <Button
                    onClick={saveSettings}
                    disabled={savingSettings}
                    className="bg-[#f43f5e] hover:bg-[#e11d48] text-white"
                  >
                    {savingSettings ? (
                      <Loader2 className="h-4 w-4 animate-spin me-2" />
                    ) : (
                      <Save className="h-4 w-4 me-2" />
                    )}
                    {language === 'ar' ? 'حفظ الإعدادات' : 'Save Settings'}
                  </Button>
                </div>
              </div>
            )}
            
            {/* Tiers Tab */}
            {activeTab === 'tiers' && (
              <div className="bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e] p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-bold text-white flex items-center gap-2">
                    <Award className="h-5 w-5 text-[#c9a227]" />
                    {language === 'ar' ? 'مستويات الولاء' : 'Loyalty Tiers'}
                  </h2>
                  <Button
                    onClick={() => {
                      setEditingTier(null);
                      setNewTier({ name_ar: '', name_en: '', min_points: 0, discount_percent: 0, color: '#c9a227' });
                      setShowTierModal(true);
                    }}
                    className="bg-[#c9a227] hover:bg-[#b8922a] text-black"
                  >
                    <Plus className="h-4 w-4 me-2" />
                    {language === 'ar' ? 'إضافة مستوى' : 'Add Tier'}
                  </Button>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {tiers.sort((a, b) => a.min_points - b.min_points).map(tier => (
                    <div
                      key={tier.id}
                      className="bg-[#0a0a0a] rounded-xl p-4 border border-[#2a2a4e] relative group"
                    >
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center mb-3"
                        style={{ backgroundColor: `${tier.color}20` }}
                      >
                        <Award className="h-6 w-6" style={{ color: tier.color }} />
                      </div>
                      <h3 className="text-white font-bold">{language === 'ar' ? tier.name_ar : tier.name_en}</h3>
                      <p className="text-[#808080] text-sm mt-1">
                        {tier.min_points.toLocaleString()} {language === 'ar' ? 'نقطة' : 'points'}
                      </p>
                      <p className="text-[#f43f5e] text-sm mt-1">
                        {tier.discount_percent}% {language === 'ar' ? 'خصم' : 'discount'}
                      </p>
                      
                      {/* Actions */}
                      <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                        <button
                          onClick={() => {
                            setEditingTier(tier);
                            setNewTier(tier);
                            setShowTierModal(true);
                          }}
                          className="w-7 h-7 bg-blue-500/20 rounded-lg flex items-center justify-center hover:bg-blue-500/30"
                        >
                          <Edit3 className="h-3 w-3 text-blue-400" />
                        </button>
                        <button
                          onClick={() => deleteTier(tier.id)}
                          className="w-7 h-7 bg-red-500/20 rounded-lg flex items-center justify-center hover:bg-red-500/30"
                        >
                          <Trash2 className="h-3 w-3 text-red-400" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Campaigns Tab */}
            {activeTab === 'campaigns' && (
              <div className="bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e] p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-bold text-white flex items-center gap-2">
                    <Gift className="h-5 w-5 text-[#a855f7]" />
                    {language === 'ar' ? 'حملات النقاط' : 'Points Campaigns'}
                  </h2>
                  <Button
                    onClick={() => {
                      setEditingCampaign(null);
                      setNewCampaign({ name_ar: '', name_en: '', multiplier: 2, start_date: '', end_date: '', description_ar: '', description_en: '', is_active: true });
                      setShowCampaignModal(true);
                    }}
                    className="bg-[#a855f7] hover:bg-[#9333ea] text-white"
                  >
                    <Plus className="h-4 w-4 me-2" />
                    {language === 'ar' ? 'إضافة حملة' : 'Add Campaign'}
                  </Button>
                </div>
                
                {campaigns.length === 0 ? (
                  <div className="text-center py-12 text-[#808080]">
                    <Gift className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>{language === 'ar' ? 'لا توجد حملات حالياً' : 'No campaigns yet'}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {campaigns.map(campaign => (
                      <div
                        key={campaign.id}
                        className="bg-[#0a0a0a] rounded-xl p-4 border border-[#2a2a4e] flex items-center justify-between"
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${campaign.is_active ? 'bg-green-500/20' : 'bg-gray-500/20'}`}>
                            <Gift className={`h-5 w-5 ${campaign.is_active ? 'text-green-400' : 'text-gray-400'}`} />
                          </div>
                          <div>
                            <h3 className="text-white font-medium">{language === 'ar' ? campaign.name_ar : campaign.name_en}</h3>
                            <p className="text-[#808080] text-sm">
                              {campaign.multiplier}x {language === 'ar' ? 'نقاط' : 'points'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-1 rounded-full ${campaign.is_active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                            {campaign.is_active ? (language === 'ar' ? 'نشط' : 'Active') : (language === 'ar' ? 'غير نشط' : 'Inactive')}
                          </span>
                          <button
                            onClick={() => {
                              setEditingCampaign(campaign);
                              setNewCampaign(campaign);
                              setShowCampaignModal(true);
                            }}
                            className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center hover:bg-blue-500/30"
                          >
                            <Edit3 className="h-4 w-4 text-blue-400" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {/* Users Tab */}
            {activeTab === 'users' && (
              <div className="bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e] p-6">
                <h2 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                  <Users className="h-5 w-5 text-[#10b981]" />
                  {language === 'ar' ? 'المستخدمين ونقاطهم' : 'Users & Points'}
                </h2>
                
                {usersWithPoints.length === 0 ? (
                  <div className="text-center py-12 text-[#808080]">
                    <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>{language === 'ar' ? 'لا يوجد مستخدمين حالياً' : 'No users yet'}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {usersWithPoints.map(user => (
                      <div
                        key={user.id}
                        className="bg-[#0a0a0a] rounded-xl p-4 border border-[#2a2a4e] flex items-center justify-between"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-lg bg-[#f43f5e]/20 flex items-center justify-center">
                            <Users className="h-5 w-5 text-[#f43f5e]" />
                          </div>
                          <div>
                            <h3 className="text-white font-medium">{user.name || user.email}</h3>
                            <p className="text-[#808080] text-sm">{user.email}</p>
                          </div>
                        </div>
                        <div className="text-end">
                          <p className="text-white font-bold">{(user.points || 0).toLocaleString()}</p>
                          <p className="text-[#808080] text-xs">{language === 'ar' ? 'نقطة' : 'points'}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
        </div>
      </main>
      
      {/* Tier Modal */}
      <Dialog open={showTierModal} onOpenChange={setShowTierModal}>
        <DialogContent className="bg-[#1a1a2e] border-[#2a2a4e] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingTier ? (language === 'ar' ? 'تعديل المستوى' : 'Edit Tier') : (language === 'ar' ? 'إضافة مستوى جديد' : 'Add New Tier')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm text-[#808080] mb-2">{language === 'ar' ? 'الاسم بالعربي' : 'Name (Arabic)'}</label>
              <Input
                value={newTier.name_ar}
                onChange={(e) => setNewTier({...newTier, name_ar: e.target.value})}
                className="bg-[#0a0a0a] border-[#2a2a4e] text-white"
              />
            </div>
            <div>
              <label className="block text-sm text-[#808080] mb-2">{language === 'ar' ? 'الاسم بالإنجليزي' : 'Name (English)'}</label>
              <Input
                value={newTier.name_en}
                onChange={(e) => setNewTier({...newTier, name_en: e.target.value})}
                className="bg-[#0a0a0a] border-[#2a2a4e] text-white"
              />
            </div>
            <div>
              <label className="block text-sm text-[#808080] mb-2">{language === 'ar' ? 'الحد الأدنى من النقاط' : 'Minimum Points'}</label>
              <Input
                type="number"
                value={newTier.min_points}
                onChange={(e) => setNewTier({...newTier, min_points: Number(e.target.value)})}
                className="bg-[#0a0a0a] border-[#2a2a4e] text-white"
              />
            </div>
            <div>
              <label className="block text-sm text-[#808080] mb-2">{language === 'ar' ? 'نسبة الخصم %' : 'Discount %'}</label>
              <Input
                type="number"
                value={newTier.discount_percent}
                onChange={(e) => setNewTier({...newTier, discount_percent: Number(e.target.value)})}
                className="bg-[#0a0a0a] border-[#2a2a4e] text-white"
              />
            </div>
            <div>
              <label className="block text-sm text-[#808080] mb-2">{language === 'ar' ? 'اللون' : 'Color'}</label>
              <input
                type="color"
                value={newTier.color}
                onChange={(e) => setNewTier({...newTier, color: e.target.value})}
                className="w-full h-10 rounded-lg cursor-pointer"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTierModal(false)} className="border-[#2a2a4e] text-white">
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button onClick={saveTier} className="bg-[#c9a227] hover:bg-[#b8922a] text-black">
              {language === 'ar' ? 'حفظ' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Campaign Modal */}
      <Dialog open={showCampaignModal} onOpenChange={setShowCampaignModal}>
        <DialogContent className="bg-[#1a1a2e] border-[#2a2a4e] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingCampaign ? (language === 'ar' ? 'تعديل الحملة' : 'Edit Campaign') : (language === 'ar' ? 'إضافة حملة جديدة' : 'Add New Campaign')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm text-[#808080] mb-2">{language === 'ar' ? 'الاسم بالعربي' : 'Name (Arabic)'}</label>
              <Input
                value={newCampaign.name_ar}
                onChange={(e) => setNewCampaign({...newCampaign, name_ar: e.target.value})}
                className="bg-[#0a0a0a] border-[#2a2a4e] text-white"
              />
            </div>
            <div>
              <label className="block text-sm text-[#808080] mb-2">{language === 'ar' ? 'الاسم بالإنجليزي' : 'Name (English)'}</label>
              <Input
                value={newCampaign.name_en}
                onChange={(e) => setNewCampaign({...newCampaign, name_en: e.target.value})}
                className="bg-[#0a0a0a] border-[#2a2a4e] text-white"
              />
            </div>
            <div>
              <label className="block text-sm text-[#808080] mb-2">{language === 'ar' ? 'مضاعف النقاط' : 'Points Multiplier'}</label>
              <Input
                type="number"
                value={newCampaign.multiplier}
                onChange={(e) => setNewCampaign({...newCampaign, multiplier: Number(e.target.value)})}
                className="bg-[#0a0a0a] border-[#2a2a4e] text-white"
              />
            </div>
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={newCampaign.is_active}
                onChange={(e) => setNewCampaign({...newCampaign, is_active: e.target.checked})}
                className="w-5 h-5 rounded border-[#2a2a4e] bg-[#0a0a0a] text-[#a855f7]"
              />
              <span className="text-white">{language === 'ar' ? 'نشط' : 'Active'}</span>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCampaignModal(false)} className="border-[#2a2a4e] text-white">
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button onClick={saveCampaign} className="bg-[#a855f7] hover:bg-[#9333ea] text-white">
              {language === 'ar' ? 'حفظ' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Redeem Code Modal */}
      <Dialog open={showRedeemModal} onOpenChange={resetRedeemModal}>
        <DialogContent className="bg-[#1a1a2e] border-[#2a2a4e] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Ticket className="h-5 w-5 text-[#c9a227]" />
              {language === 'ar' ? 'استرداد الرمز' : 'Redeem Code'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            {!redeemResult ? (
              <div className="space-y-4">
                <p className="text-[#808080] text-sm">
                  {language === 'ar' 
                    ? 'أدخل رمز الولاء لاسترداد النقاط أو المكافآت'
                    : 'Enter the loyalty code to redeem points or rewards'}
                </p>
                
                <div>
                  <label className="block text-sm text-[#808080] mb-2">
                    {language === 'ar' ? 'رمز الاسترداد' : 'Redemption Code'}
                  </label>
                  <Input
                    value={redeemCode}
                    onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
                    onKeyDown={(e) => e.key === 'Enter' && redeemCodeHandler()}
                    placeholder={language === 'ar' ? 'مثال: LOYALTY2024' : 'e.g., LOYALTY2024'}
                    className="bg-[#0a0a0a] border-[#2a2a4e] text-white text-center text-lg tracking-widest uppercase"
                    data-testid="redeem-code-input"
                  />
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                {redeemResult.success ? (
                  <div className="space-y-4">
                    <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
                      <CheckCircle className="h-8 w-8 text-green-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">
                        {language === 'ar' ? 'تم الاسترداد بنجاح!' : 'Successfully Redeemed!'}
                      </h3>
                      <div className="bg-[#c9a227]/20 rounded-xl p-4 mb-3">
                        <p className="text-3xl font-bold text-[#c9a227]">
                          +{redeemResult.points?.toLocaleString() || 0}
                        </p>
                        <p className="text-[#808080] text-sm">
                          {language === 'ar' ? 'نقطة' : 'points'}
                        </p>
                      </div>
                      {redeemResult.user && (
                        <p className="text-[#808080] text-sm">
                          {language === 'ar' ? 'تمت الإضافة لـ:' : 'Added to:'} {redeemResult.user}
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto">
                      <X className="h-8 w-8 text-red-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">
                        {language === 'ar' ? 'فشل الاسترداد' : 'Redemption Failed'}
                      </h3>
                      <p className="text-[#808080]">
                        {redeemResult.message}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <DialogFooter>
            {!redeemResult ? (
              <>
                <Button variant="outline" onClick={resetRedeemModal} className="border-[#2a2a4e] text-white">
                  {language === 'ar' ? 'إلغاء' : 'Cancel'}
                </Button>
                <Button 
                  onClick={redeemCodeHandler} 
                  disabled={redeemLoading || !redeemCode.trim()}
                  className="bg-gradient-to-r from-[#c9a227] to-[#f5d742] hover:from-[#b8922a] hover:to-[#e5c732] text-black"
                  data-testid="redeem-submit-btn"
                >
                  {redeemLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin me-2" />
                  ) : (
                    <Ticket className="h-4 w-4 me-2" />
                  )}
                  {language === 'ar' ? 'استرداد' : 'Redeem'}
                </Button>
              </>
            ) : (
              <Button 
                onClick={resetRedeemModal} 
                className="w-full bg-[#2a2a4e] hover:bg-[#3a3a5e] text-white"
              >
                {language === 'ar' ? 'إغلاق' : 'Close'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
